import type { MouseEventHandler } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { Share16 } from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { ShareDropdown, Tooltip } from '@elfsight/widget-modal';
import { useTranslation } from 'react-i18next';
import { useSocialFeedSettings } from '../../utils';

interface ShareButtonProps {
  url: string;
  color: string;
  isTitleShown?: boolean;
  isShareButtonDropdownOpen?: boolean;
  setIsDropDownOpen?: (value: boolean) => void;
}

export function ShareButton({
  color,
  url,
  setIsDropDownOpen,
  isTitleShown = true,
  isShareButtonDropdownOpen = false,
  ...forwardingProps
}: ShareButtonProps) {
  const { t } = useTranslation();
  const {
    shareDropdownBgColor,
    shareDropdownTextColor,
    tooltipBackgroundColor,
    tooltipTextColor
  } = useSocialFeedSettings();

  const [isOpen, setIsOpen] = useState(isShareButtonDropdownOpen);
  const [anchorElement, setAnchorElement] = useState<Element | null>(null);

  const handleShareOpen: MouseEventHandler = (e) => {
    e.stopPropagation();
    setIsOpen(true);

    setIsDropDownOpen?.(true);
  };

  const handleShareClose = () => {
    setIsOpen(false);

    setIsDropDownOpen?.(false);
  };

  const shareButtonLabel = t('Share');

  return (
    <div>
      <Tooltip
        title={t('Share Post')}
        disabled={isOpen}
        background={tooltipBackgroundColor}
        fontColor={tooltipTextColor}
      >
        <Button
          {...forwardingProps}
          ref={setAnchorElement}
          aria-label={shareButtonLabel}
          onClick={handleShareOpen}
          tabIndex={0}
          _textColor={color}
        >
          <Icon component={Share16} size={16} fill={color} />
          {isTitleShown && <div>{shareButtonLabel}</div>}
        </Button>
      </Tooltip>

      <ShareDropdown
        open={isOpen}
        onCloseRequest={handleShareClose}
        alignment="center"
        alphaBoxShadow={0.3}
        anchorElement={anchorElement}
        background={shareDropdownBgColor}
        popoutDirection="upward"
        displayOver
        fontColor={shareDropdownTextColor}
        fontSize={14}
        iconSize={20}
        link={url}
        width="210px"
      />
    </div>
  );
}

const Button = styled.button<{ _textColor: string }>`
  border: none;
  outline: none;
  background: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 13px;
  opacity: 0.7;
  color: ${({ _textColor }) => _textColor};

  &:hover {
    opacity: 1;
  }

  & *:not(:first-child) {
    margin-left: 4px;
  }
`;

/*
  I18N FIX
*/

// t(`Copy Link`)
// t(`Link Copied`)
// t(`Share on Facebook`)
// t(`Share on WhatsApp`)
// t(`Share on Telegram`)
// t(`Share via Email`)
// t(`Share on X`)
// t(`Share on LinkedIn`)
